import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Container,
  InputAdornment, TextField, Typography
} from '@mui/material';
import { Business, Email, Lock, Person, Phone } from '@mui/icons-material';

const SupplierInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const loadInvite = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/suppliers/invite/${token}`);
        setInvite(data.data);
        setFormData((prev) => ({ ...prev, phone: data.data.phone || '' }));
      } catch (err) {
        setError(err.response?.data?.error || 'Invitation link is invalid or has expired.');
      }
      setLoading(false);
    };

    loadInvite();
  }, [token]);

  const handleChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/suppliers/invite/${token}/register`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        password: formData.password
      });
      setSuccess('Your supplier portal access is ready. Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate supplier portal access.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Card elevation={4} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={3}>
            <Typography variant="h4" fontWeight={800} color="primary">OSESG</Typography>
            <Typography variant="h6" color="text.secondary">Supplier Portal Setup</Typography>
            <Typography variant="body2" color="text.secondary">
              Create the supplier portal password for {invite?.companyName || 'your company'}.
            </Typography>
          </Box>

          {(error || success) && (
            <Alert severity={error ? 'error' : 'success'} sx={{ mb: 2 }}>
              {error || success}
            </Alert>
          )}

          {!error && invite && (
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Company"
                value={invite.companyName}
                sx={{ mb: 2 }}
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start"><Business /></InputAdornment>
                }}
              />

              <TextField
                fullWidth
                label="Login Email"
                value={invite.email || ''}
                sx={{ mb: 2 }}
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start"><Email /></InputAdornment>
                }}
                helperText="This invited email address will be your supplier portal username."
              />

              <TextField
                fullWidth
                name="firstName"
                label="First Name"
                value={formData.firstName}
                onChange={handleChange}
                sx={{ mb: 2 }}
                required
                InputProps={{ startAdornment: <InputAdornment position="start"><Person /></InputAdornment> }}
              />

              <TextField
                fullWidth
                name="lastName"
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                sx={{ mb: 2 }}
                required
              />

              <TextField
                fullWidth
                name="phone"
                label="Phone"
                value={formData.phone}
                onChange={handleChange}
                sx={{ mb: 2 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Phone /></InputAdornment> }}
              />

              <TextField
                fullWidth
                name="password"
                label="Create Password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                sx={{ mb: 2 }}
                required
                InputProps={{ startAdornment: <InputAdornment position="start"><Lock /></InputAdornment> }}
              />

              <TextField
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                sx={{ mb: 3 }}
                required
                InputProps={{ startAdornment: <InputAdornment position="start"><Lock /></InputAdornment> }}
              />

              <Button type="submit" fullWidth variant="contained" size="large" disabled={submitting} sx={{ py: 1.5 }}>
                {submitting ? <CircularProgress size={22} color="inherit" /> : 'Activate Supplier Portal Access'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default SupplierInvitePage;